class QuickOrderForm extends HTMLElement {
  static selectors = {
    form:            '.quick-order-form',
    brandFilter:     '.js-quick-order-brand-filter',
    addSelected:     '.js-quick-order-add-selected',
    addSelectedText: '.js-quick-order-add-selected-text',
    addAll:          '.js-quick-order-add-all',
    qtyInput:        '.js-quick-order-qty',
    qtyMinus:        '.js-quick-order-qty-minus',
    qtyPlus:         '.js-quick-order-qty-plus',
    productRow:      '.js-quick-order-product-row',
    variantRow:      '.quick-order-variant-row',
    showingCount:    '.js-quick-order-showing',
    qtyWrapper:      '.quick-order-qty',
    pagination:      '.quick-order-form__pagination',
    loading:         '.quick-order-form__loading',
    pageButton:      '.js-quick-order-page',
  };

  // Products per page once everything is merged and regrouped by vendor
  static pageSize = 160;

  constructor() {
    super();
    this.allBlocks = [];
    this.vendorBlocks = new Map();
    this.currentVendor = '';
  }

  connectedCallback() {
    this.bindEvents();
    this.initPagination();
  }

  find(selector, scope = this) {
    return scope.querySelector(selector);
  }

  findAll(selector, scope = this) {
    return [...scope.querySelectorAll(selector)];
  }

  parseQty(input) {
    return Math.max(0, parseInt(input.value, 10) || 0);
  }

  escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  getTrackedQtyInputs(vendor) {
    const { selectors } = QuickOrderForm;
    if (!this.allBlocks.length) return this.findAll(selectors.qtyInput);
    return this.getFilteredBlocks(vendor)
      .flatMap(b => b.rows)
      .map(row => row.querySelector(selectors.qtyInput))
      .filter(Boolean);
  }

  getSelectedItems() {
    return this.getTrackedQtyInputs()
      .filter(input => this.parseQty(input) > 0)
      .map(input => ({ id: parseInt(input.dataset.id, 10), quantity: this.parseQty(input) }));
  }

  updateSelectedCount() {
    const { selectors } = QuickOrderForm;
    const count = this.getTrackedQtyInputs().reduce((sum, i) => sum + this.parseQty(i), 0);
    this.findAll(selectors.addSelectedText).forEach(el => {
      el.textContent = count > 0
        ? `Add Selected To Basket (${count})`
        : 'Add Selected To Basket';
    });
  }

  // Load & group every paginated page
  getRowKey(row) {
    const { selectors } = QuickOrderForm;
    const quantityInput = row.querySelector(selectors.qtyInput);
    if (quantityInput) return `variant:${quantityInput.dataset.id}`;
    const productLink = row.querySelector('.quick-order-col--name a');
    return productLink ? `product:${productLink.getAttribute('href')}` : null;
  }

  getRowTitle(row) {
    return row.querySelector('.quick-order-col--name a')?.textContent.trim() || '';
  }

  getPaginationUrls() {
    const { selectors } = QuickOrderForm;
    return this.findAll(`${selectors.pagination} a[href]`)
      .map(link => new URL(link.href, window.location.href).href);
  }

  async fetchAllRows(tableBody, initialUrls) {
    const { selectors } = QuickOrderForm;
    const knownRows = new Set(
      this.findAll(selectors.productRow, tableBody).map(row => this.getRowKey(row)).filter(Boolean)
    );
    const queue = [...new Set(initialUrls)];
    const visited = new Set();

    while (queue.length) {
      const url = queue.shift();
      if (visited.has(url)) continue;
      visited.add(url);

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Page load failed: ${response.status}`);

      const html = await response.text();
      const page = new DOMParser().parseFromString(html, 'text/html');
      const pageBody = page.querySelector(`${selectors.form} tbody`);
      if (!pageBody) continue;

      pageBody.querySelectorAll(selectors.productRow).forEach(row => {
        const key = this.getRowKey(row);
        if (!key || knownRows.has(key)) return;
        knownRows.add(key);
        tableBody.appendChild(document.importNode(row, true));
      });

      page.querySelectorAll(`${selectors.pagination} a[href]`).forEach(link => {
        const nextUrl = new URL(link.href, window.location.href).href;
        if (!visited.has(nextUrl)) queue.push(nextUrl);
      });
    }
  }

  // Groups a product row (or a header row + its following variant rows) into one
  buildProductBlocks(tableBody) {
    const { selectors } = QuickOrderForm;
    const blocks = [];
    let current = null;

    this.findAll(selectors.productRow, tableBody).forEach(row => {
      if (row.matches(selectors.variantRow) && current) {
        current.rows.push(row);
        return;
      }
      current = { vendor: row.dataset.vendor, title: this.getRowTitle(row), rows: [row] };
      blocks.push(current);
    });

    return blocks;
  }

  buildVendorHeadingRow(vendor, count) {
    const tr = document.createElement('tr');
    tr.className = 'quick-order-vendor-heading-row js-quick-order-vendor-row';
    tr.dataset.vendor = vendor;
    const label = count === 1 ? 'product' : 'products';
    tr.innerHTML = `<td colspan="6" class="quick-order-vendor-heading"><strong>${this.escapeHtml(vendor)}</strong> <span class="quick-order-vendor-count">&ndash; ${count} ${label}</span></td>`;
    return tr;
  }

  rebuildBrandFilterOptions(vendors) {
    const { selectors } = QuickOrderForm;
    const filter = this.find(selectors.brandFilter);
    if (!filter) return;

    const currentValue = filter.value;
    filter.innerHTML = ['<option value="">All Brands</option>']
      .concat(vendors.map(v => `<option value="${this.escapeHtml(v)}">${this.escapeHtml(v)}</option>`))
      .join('');
    if (vendors.includes(currentValue)) filter.value = currentValue;
  }

  // Pagination
  buildPageNumbers(current, total) {
    const delta = 2;
    const range = [];
    const withDots = [];
    let last;

    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) range.push(i);
    }

    range.forEach(i => {
      if (last) {
        if (i - last === 2) withDots.push(last + 1);
        else if (i - last > 2) withDots.push('…');
      }
      withDots.push(i);
      last = i;
    });

    return withDots;
  }

  renderPaginationControls(totalPages, current) {
    const { selectors } = QuickOrderForm;
    const container = this.find(selectors.pagination);
    if (!container) return;

    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    const pageItems = this.buildPageNumbers(current, totalPages).map(p => {
      if (p === '…') return '<li><span class="pagination__item page">&hellip;</span></li>';
      return p === current
        ? `<li><span class="pagination__item pagination__item--current page current" aria-current="page">${p}</span></li>`
        : `<li><button type="button" class="pagination__item link page js-quick-order-page" data-page="${p}">${p}</button></li>`;
    }).join('');

    const prevItem = current > 1
      ? `<li><button type="button" class="pagination__item prev pagination__item--prev pagination__item-arrow link js-quick-order-page" data-page="${current - 1}" aria-label="Previous page">&larr;</button></li>`
      : '';
    const nextItem = current < totalPages
      ? `<li><button type="button" class="pagination__item next pagination__item--next pagination__item-arrow link js-quick-order-page" data-page="${current + 1}" aria-label="Next page">&rarr;</button></li>`
      : '';

    container.innerHTML = `
      <nav class="pagination" role="navigation" aria-label="Pagination">
        <ul class="pagination__list list-unstyled pagination__items" role="list">
          ${prevItem}${pageItems}${nextItem}
        </ul>
      </nav>`;
  }

  updateShowingCount(total, start, count) {
    const { selectors } = QuickOrderForm;
    const countEl = this.find(selectors.showingCount);
    if (!countEl) return;
    if (!total) { countEl.textContent = '0'; return; }
    countEl.textContent = total > count ? `${start + 1}–${start + count} of ${total}` : `${total}`;
  }

  getFilteredBlocks(vendor) {
    return vendor ? (this.vendorBlocks.get(vendor) || []) : this.allBlocks;
  }

  // Renders only the requested page's rows into the table
  renderPage(page, vendor) {
    const tableBody = this.find('tbody');
    if (!tableBody) return;

    const filtered = this.getFilteredBlocks(vendor);
    const totalPages = Math.max(1, Math.ceil(filtered.length / QuickOrderForm.pageSize));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const start = (safePage - 1) * QuickOrderForm.pageSize;
    const pageBlocks = filtered.slice(start, start + QuickOrderForm.pageSize);

    const fragment = document.createDocumentFragment();
    let lastVendor = null;

    pageBlocks.forEach(block => {
      if (block.vendor !== lastVendor) {
        fragment.appendChild(this.buildVendorHeadingRow(block.vendor, this.vendorBlocks.get(block.vendor)?.length || 0));
        lastVendor = block.vendor;
      }
      block.rows.forEach(row => fragment.appendChild(row));
    });

    tableBody.innerHTML = '';
    tableBody.appendChild(fragment);

    this.currentVendor = vendor;

    this.updateShowingCount(filtered.length, start, pageBlocks.length);
    this.renderPaginationControls(totalPages, safePage);
  }

  async initPagination() {
    const { selectors } = QuickOrderForm;
    const tableBody = this.find('tbody');
    const loading   = this.find(selectors.loading);
    const filter    = this.find(selectors.brandFilter);
    if (!tableBody) return;

    const paginationUrls = this.getPaginationUrls();

    if (paginationUrls.length) {
      if (loading) loading.hidden = false;
      if (filter) filter.disabled = true;

      try {
        await this.fetchAllRows(tableBody, paginationUrls);
      } catch (error) {
        console.error('Quick Order Form: Could not load all products', error);
      } finally {
        if (loading) loading.hidden = true;
        if (filter) filter.disabled = false;
      }
    }

    this.allBlocks = this.buildProductBlocks(tableBody);
    this.allBlocks.sort((a, b) => a.vendor.localeCompare(b.vendor) || a.title.localeCompare(b.title));

    this.vendorBlocks = new Map();
    this.allBlocks.forEach(block => {
      if (!this.vendorBlocks.has(block.vendor)) this.vendorBlocks.set(block.vendor, []);
      this.vendorBlocks.get(block.vendor).push(block);
    });

    this.rebuildBrandFilterOptions([...this.vendorBlocks.keys()]);
    this.renderPage(1, '');
  }

  // Cart submission
  setButtonState(btn, loading) {
    btn.disabled = loading;
    btn.classList.toggle('is-adding', loading);
  }

  // Locks every add to bag trigger while a request is in progress
  setFormBusy(busy) {
    const { selectors } = QuickOrderForm;
    this.findAll(`${selectors.addAll}, ${selectors.addSelected}`).forEach(btn => { btn.disabled = busy; });
    this.getTrackedQtyInputs().forEach(input => { input.disabled = busy; });
    this.findAll(`${selectors.qtyMinus}, ${selectors.qtyPlus}`).forEach(btn => { btn.disabled = busy; });
  }

  resetForm() {
    this.getTrackedQtyInputs().forEach(input => { input.value = 0; });
    this.updateSelectedCount();
  }

  openCartDrawer() {
    if (typeof ajaxCart !== 'undefined' && typeof ajaxCart.load === 'function') {
      ajaxCart.load();
    }
    if (typeof jQuery !== 'undefined' && typeof jQuery.fn.magnificPopup !== 'undefined') {
      jQuery.magnificPopup.open({
        items: { src: '.js-cart-draw' },
        type: 'inline',
        mainClass: 'mfp-medium',
        fixedContentPos: true,
        midClick: true,
        closeMarkup: '<button title="Close (Esc)" type="button" class="mfp-close mfp-close--custom js-close-mfp" aria-label="close"><i class="icon icon--close"></i></button>',
        removalDelay: 200,
      });
    }
  }

  async addToCart(items, btn) {
    this.setButtonState(btn, true);
    this.setFormBusy(true);

    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) throw new Error(`Cart error ${res.status}`);

      btn.classList.add('is-added');
      btn.addEventListener('animationend', () => btn.classList.remove('is-added'), { once: true });

      this.resetForm();
      this.openCartDrawer();
    } catch (err) {
      console.error('Quick Order Form:', err);
    } finally {
      this.setButtonState(btn, false);
      this.setFormBusy(false);
    }
  }

  bindEvents() {
    const { selectors } = QuickOrderForm;

    const filter = this.find(selectors.brandFilter);
    filter?.addEventListener('change', () => this.renderPage(1, filter.value));

    this.addEventListener('click', (e) => {
      const pageBtn = e.target.closest(selectors.pageButton);
      if (!pageBtn) return;
      const page = parseInt(pageBtn.dataset.page, 10);
      if (Number.isNaN(page)) return;
      this.renderPage(page, this.currentVendor);
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    this.addEventListener('click', (e) => {
      const minus = e.target.closest(selectors.qtyMinus);
      const plus  = e.target.closest(selectors.qtyPlus);
      if (!minus && !plus) return;

      e.stopImmediatePropagation();

      const wrapper = (minus || plus).closest(selectors.qtyWrapper);
      const input   = wrapper?.querySelector(selectors.qtyInput);
      if (!input) return;

      const current = this.parseQty(input);
      input.value   = plus ? current + 1 : Math.max(0, current - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    this.addEventListener('change', (e) => {
      if (!e.target.matches(selectors.qtyInput)) return;
      if (this.parseQty(e.target) < 0) e.target.value = 0;
      this.updateSelectedCount();
    });

    this.findAll(selectors.addSelected).forEach(btn => {
      btn.addEventListener('click', () => {
        const items = this.getSelectedItems();
        if (items.length) this.addToCart(items, btn);
      });
    });

    this.findAll(selectors.addAll).forEach(btn => {
      btn.addEventListener('click', () => {
        this.getTrackedQtyInputs(this.currentVendor).forEach(input => {
          if (!input.disabled && this.parseQty(input) === 0) input.value = 1;
        });
        this.updateSelectedCount();

        const items = this.getSelectedItems();
        if (items.length) this.addToCart(items, btn);
      });
    });
  }
}

customElements.define('quick-order-form', QuickOrderForm);
